import {
  IScreenCollectionSchedule,
  ISignageScreen,
} from '../models/signage-model';

export function createScheduleId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `sched_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

export function timeToMinutes(time: string): number {
  const match = /^(\d{1,2}):(\d{2})$/.exec(String(time ?? '').trim());
  if (!match) {
    return -1;
  }
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
    return -1;
  }
  return hours * 60 + minutes;
}

export function formatTimeFromDate(date: Date | null | undefined): string {
  if (!date || Number.isNaN(date.getTime())) {
    return '09:00';
  }
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
}

export function parseTimeToDate(time: string, fallback = '09:00'): Date {
  const value = time || fallback;
  const [hours, minutes] = value.split(':').map(Number);
  const date = new Date();
  date.setHours(hours, minutes, 0, 0);
  return date;
}

export function mapSchedulesFromFirebase(data: unknown): IScreenCollectionSchedule[] {
  if (!data || typeof data !== 'object') {
    return [];
  }
  return Object.entries(data as Record<string, Omit<IScreenCollectionSchedule, 'id'>>).map(
    ([id, schedule]) => ({ id, ...schedule }),
  );
}

export function mapSchedulesToFirebase(
  schedules: IScreenCollectionSchedule[]
): Record<string, Omit<IScreenCollectionSchedule, 'id'>> {
  const result: Record<string, Omit<IScreenCollectionSchedule, 'id'>> = {};
  for (const schedule of schedules) {
    const { id: scheduleId, ...payload } = schedule;
    result[scheduleId || createScheduleId()] = payload;
  }
  return result;
}

export function normalizeSignageScreen(raw: Record<string, unknown>, screenKey: string): ISignageScreen {
  return {
    key: screenKey,
    name: String(raw['name'] ?? ''),
    isPublished: Boolean(raw['isPublished']),
    collectionSchedules: mapSchedulesFromFirebase(raw['collectionSchedules']),
  };
}

function matchesCollectionKey(schedule: IScreenCollectionSchedule, collectionKey: string): boolean {
  return String(schedule.slideCollectionKey) === String(collectionKey);
}

export function scheduleReferencesCollection(
  schedules: IScreenCollectionSchedule[],
  collectionKey: string,
): boolean {
  return schedules.some(schedule => matchesCollectionKey(schedule, collectionKey));
}

export function renameCollectionInSchedules(
  schedules: IScreenCollectionSchedule[],
  collectionKey: string,
  newName: string,
): IScreenCollectionSchedule[] {
  return schedules.map(schedule =>
    matchesCollectionKey(schedule, collectionKey)
      ? { ...schedule, slideCollectionName: newName }
      : schedule,
  );
}

export function removeCollectionFromSchedules(
  schedules: IScreenCollectionSchedule[],
  collectionKey: string,
): IScreenCollectionSchedule[] {
  return schedules.filter(schedule => !matchesCollectionKey(schedule, collectionKey));
}

function isScheduleTimeRangeValid(startTime: string, endTime: string): boolean {
  const start = timeToMinutes(startTime);
  const end = timeToMinutes(endTime);
  return start >= 0 && end > start;
}

function isScheduleRowValid(schedule: IScreenCollectionSchedule): boolean {
  return (
    Boolean(schedule.slideCollectionKey) &&
    schedule.days.length > 0 &&
    isScheduleTimeRangeValid(schedule.startTime, schedule.endTime)
  );
}

export function hasValidScheduleRow(schedules: IScreenCollectionSchedule[]): boolean {
  return schedules.some(isScheduleRowValid);
}

export function screenHasValidSchedules(screen: ISignageScreen): boolean {
  return hasValidScheduleRow(screen.collectionSchedules ?? []);
}

export function applySchedulesToScreen(
  screen: ISignageScreen,
  collectionSchedules: IScreenCollectionSchedule[],
): ISignageScreen {
  const next: ISignageScreen = { ...screen, collectionSchedules };
  if (!screenHasValidSchedules(next)) {
    next.isPublished = false;
  }
  return next;
}

export function validateSchedules(schedules: IScreenCollectionSchedule[]): string | null {
  if (!schedules.length) {
    return 'SCREEN_SCHEDULE_REQUIRED';
  }
  for (const schedule of schedules) {
    if (!schedule.slideCollectionKey) {
      return 'SCREEN_SCHEDULE_COLLECTION_REQUIRED';
    }
    if (!schedule.days.length) {
      return 'SCREEN_SCHEDULE_DAYS_REQUIRED';
    }
    if (!isScheduleTimeRangeValid(schedule.startTime, schedule.endTime)) {
      return 'SCREEN_SCHEDULE_INVALID_TIME';
    }
  }
  return null;
}

export function getScreenScheduleSummary(screen: ISignageScreen): string {
  const count = screen.collectionSchedules?.length ?? 0;
  if (!count) {
    return '';
  }
  if (count === 1) {
    const s = screen.collectionSchedules[0];
    return `${s.slideCollectionName || s.slideCollectionKey} · ${s.startTime}–${s.endTime}`;
  }
  return `${count}`;
}

/** Card label: single schedule detail, localized count, or empty-state hint. */
export function buildScreenCardScheduleLabel(
  screen: ISignageScreen,
  multiScheduleLabel: string,
  emptyScheduleLabel: string,
): string {
  const count = screen.collectionSchedules?.length ?? 0;
  if (count > 1) {
    return multiScheduleLabel.replace('{count}', String(count));
  }
  return getScreenScheduleSummary(screen) || emptyScheduleLabel;
}

export interface ScheduleFormRow extends IScreenCollectionSchedule {
  startTimeDate: Date;
  endTimeDate: Date;
}

export function scheduleToFormRow(schedule: IScreenCollectionSchedule): ScheduleFormRow {
  return {
    ...schedule,
    startTimeDate: parseTimeToDate(schedule.startTime),
    endTimeDate: parseTimeToDate(schedule.endTime),
  };
}

export function formRowToSchedule(row: ScheduleFormRow): IScreenCollectionSchedule {
  return {
    id: row.id,
    slideCollectionKey: row.slideCollectionKey,
    slideCollectionName: row.slideCollectionName,
    days: row.days,
    startTime: formatTimeFromDate(row.startTimeDate),
    endTime: formatTimeFromDate(row.endTimeDate),
  };
}

export function createEmptyScheduleRow(): ScheduleFormRow {
  return scheduleToFormRow({
    id: createScheduleId(),
    slideCollectionKey: '',
    slideCollectionName: '',
    days: [],
    startTime: '09:00',
    endTime: '17:00',
  });
}
